import { Box, Flex, styled } from '@withjoy/joykit';
import type { ReactNode } from 'react';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { LazyStatusBannerConfetti } from './StatusBannerConfetti.lazy';

export const StyledStatusBanner = (props: { playConfetti?: boolean; children: ReactNode }) => {
  const { children, playConfetti } = props;
  const toastRef = useRef<HTMLDivElement>(null);
  const [confettiActive, setConfettiActive] = useState(playConfetti);
  const [confettiPosition, setConfettiPosition] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (toastRef.current) {
      const position = toastRef.current.getBoundingClientRect();

      setReady(true);
      setConfettiPosition({
        x: position.right - position.width / 2,
        y: position.y,
      });
    }
  }, []);

  const canShowConfetti = typeof window === 'object' && ready;

  return (
    <>
      <StatusBannerContainer>
        {confettiActive &&
          canShowConfetti &&
          createPortal(
            // No boundary sits between StatusBanner and the app root, so a confetti chunk that
            // still fails after loadable's retries would otherwise blank the guest site. The
            // burst is decorative: on failure render nothing and keep the banner.
            <ChunkErrorBoundary
              section="guest/statusBannerConfetti"
              fallback={null}
            >
              <LazyStatusBannerConfetti
                source={confettiPosition}
                onComplete={() => setConfettiActive(false)}
              />
            </ChunkErrorBoundary>,
            document.body,
          )}

        <StatusBannerInner ref={toastRef}>{children}</StatusBannerInner>
      </StatusBannerContainer>
    </>
  );
};

const StatusBannerContainer = styled(Flex)`
  z-index: ${props => Number(props.theme.zIndices.banner) - 2};
  position: fixed;

  width: 100%;
  height: fit-content;

  padding: 0px;
  margin: 0px;

  align-items: center;
  justify-content: center;

  top: 40px;
  left: 0px;
`;

const StatusBannerInner = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;

  z-index: ${props => props.theme.zIndices.banner};

  padding: 16px 24px 16px 24px;

  width: 100%;

  max-width: 750px;
  margin: 0px 14px 0px 14px;

  border-radius: 12px;

  background-color: white;
  box-shadow:
    0px 10px 60px -20px rgb(44, 41, 37),
    0px 9px 36px -30px rgb(0, 0, 0);
`;
